import React, { useState, useRef, useEffect } from 'react';
import { Card, Label, Button, Icon, Grid, Image, Popup, List } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { signS3UrlForUser } from '../../utils/fetchSignedUrlByFolderV2';

/**
 * Helper: resolve actor display name with multiple fallbacks
 */
const getActorDisplayName = (actor) => {
  if (!actor) return 'Unknown Actor';
  if (actor.firstName && actor.lastName) return `${actor.firstName} ${actor.lastName}`;
  if (actor.firstName) return actor.firstName;
  if (actor.name) return actor.name;
  if (actor.displayName) return actor.displayName;
  return 'Unknown Actor';
};

/**
 * Helper: check if a URL is an S3 URL that needs signing
 */
const isS3Url = (url) => {
  if (!url) return false;
  return url.includes('s3.') && url.includes('amazonaws.com');
};

/**
 * Inline audio player for a single sample with S3 URL signing
 */
const SampleAudioItem = ({ sample, userId }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [signedAudioUrl, setSignedAudioUrl] = useState(null);

  const rawAudioUrl = sample.voiceS3Url || sample.s3Url;
  const sampleName = sample.name || sample.characterName || 'Voice Sample';

  // Sign the audio URL on mount
  useEffect(() => {
    const signUrl = async () => {
      if (!rawAudioUrl || !userId) return;
      if (isS3Url(rawAudioUrl)) {
        try {
          const signed = await signS3UrlForUser(userId, rawAudioUrl);
          setSignedAudioUrl(signed);
        } catch (err) {
          console.error('Failed to sign audio URL:', err);
          setSignedAudioUrl(rawAudioUrl); // fallback to raw
        }
      } else {
        setSignedAudioUrl(rawAudioUrl);
      }
    };
    signUrl();
  }, [rawAudioUrl, userId]);

  const togglePlay = () => {
    if (!audioRef.current || !signedAudioUrl) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().catch(() => setIsPlaying(false));
      setIsPlaying(true);
    }
  };

  const handleEnded = () => setIsPlaying(false);

  return (
    <List.Item style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' }}>
      {rawAudioUrl ? (
        <>
          <Button
            icon={isPlaying ? 'pause' : 'play'}
            size="mini"
            circular
            color={isPlaying ? 'orange' : 'blue'}
            onClick={togglePlay}
            disabled={!signedAudioUrl}
            style={{ flexShrink: 0 }}
          />
          {signedAudioUrl && (
            <audio ref={audioRef} src={signedAudioUrl} onEnded={handleEnded} preload="none" />
          )}
        </>
      ) : (
        <Icon name="file audio outline" color="grey" style={{ flexShrink: 0 }} />
      )}
      <span style={{ fontSize: '0.92em' }}>{sampleName}</span>
      {sample.fileDuration > 0 && (
        <span style={{ fontSize: '0.8em', color: '#999', marginLeft: 'auto' }}>
          {Math.round(sample.fileDuration)}s
        </span>
      )}
    </List.Item>
  );
};

/**
 * ApplicationCard component displays a single application in a card format
 */
const ApplicationCard = ({
  application,
  onViewDetails,
  onAccept,
  onReject,
  onMessage,
  isCreator = false,
  viewMode = 'standard',
  userId = null
}) => {
  const [signedProfileUrl, setSignedProfileUrl] = useState(null);

  // Default profile image
  const defaultProfileImage = 'https://react.semantic-ui.com/images/avatar/large/matthew.png';

  // Format the submission date if it exists
  const formattedSubmissionDate = application.submissionDate
    ? format(new Date(application.submissionDate), 'MMM d, yyyy')
    : 'Unknown date';

  // Get the status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Accepted': return 'green';
      case 'Rejected': return 'red';
      case 'Pending': return 'yellow';
      default: return 'grey';
    }
  };

  // Get actor display name with fallbacks
  const actorName = getActorDisplayName(application.actor);

  // Sign profile picture URL
  useEffect(() => {
    const signProfilePicture = async () => {
      const profilePicUrl = application.actor?.profilePictureUrl;
      if (!profilePicUrl || !userId) return;

      if (isS3Url(profilePicUrl)) {
        try {
          const signed = await signS3UrlForUser(userId, profilePicUrl);
          setSignedProfileUrl(signed);
        } catch (err) {
          console.error('Failed to sign profile picture URL:', err);
        }
      } else {
        // Non-S3 URL, use directly
        setSignedProfileUrl(profilePicUrl);
      }
    };
    signProfilePicture();
  }, [application.actor?.profilePictureUrl, userId]);

  // Resolved profile image
  const profileImage = signedProfileUrl || defaultProfileImage;

  // Render a compact card
  if (viewMode === 'compact') {
    return (
      <Card fluid>
        <Card.Content>
          <Card.Header>{actorName}</Card.Header>
          <Card.Meta>
            <Label color={getStatusColor(application.status)} size="tiny">
              {application.status}
            </Label>
            <span style={{ marginLeft: '10px' }}>
              <Icon name="calendar" /> {formattedSubmissionDate}
            </span>
          </Card.Meta>
        </Card.Content>
        <Card.Content extra>
          <Button
            primary
            size="tiny"
            onClick={() => onViewDetails(application.uuid)}
            fluid
          >
            View Details
          </Button>
        </Card.Content>
      </Card>
    );
  }

  // Render a standard/detailed card
  return (
    <Card fluid>
      <Card.Content>
        <Grid>
          <Grid.Column width={3} textAlign="center">
            <Image
              src={profileImage}
              circular
              style={{ width: '100px', height: '100px', objectFit: 'cover' }}
            />
          </Grid.Column>
          <Grid.Column width={13}>
            <Card.Header style={{ marginBottom: '6px' }}>
              <Label color={getStatusColor(application.status)} size="tiny" style={{ marginRight: '10px' }}>
                {application.status}
              </Label>
              {actorName}
            </Card.Header>
            <Card.Meta>
              <div style={{ textAlign: 'left' }}>
                <Icon name="calendar" /> Submitted: {formattedSubmissionDate}
              </div>
            </Card.Meta>
            <Card.Description style={{ marginTop: '8px' }}>
              {viewMode === 'detailed'
                ? application.notes
                : application.notes?.length > 150
                  ? `${application.notes.substring(0, 150)}...`
                  : application.notes || 'No notes provided'}
            </Card.Description>
          </Grid.Column>
        </Grid>
      </Card.Content>

      {/* Samples section — lightweight list with play buttons */}
      {application.samples && application.samples.length > 0 && (
        <Card.Content extra>
          <div style={{ marginBottom: '4px' }}>
            <Icon name="microphone" />
            <strong>Voice Samples ({application.samples.length})</strong>
          </div>
          <List divided relaxed="very" style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {application.samples.map(sample => (
              <SampleAudioItem key={sample.uuid} sample={sample} userId={userId} />
            ))}
          </List>
        </Card.Content>
      )}

      <Card.Content extra>
        <div className="ui buttons">
          <Button
            primary
            onClick={() => onViewDetails(application.uuid)}
          >
            View Details
          </Button>
          {isCreator && onMessage && (
            <Button
              color="blue"
              onClick={() => onMessage(application)}
            >
              <Icon name="mail" /> Message
            </Button>
          )}
          {isCreator && application.status === 'Pending' && (
            <>
              <Button
                color="green"
                onClick={() => onAccept(application.uuid)}
              >
                Accept
              </Button>
              <Button
                color="red"
                onClick={() => onReject(application.uuid)}
              >
                Reject
              </Button>
            </>
          )}
          {isCreator && application.status === 'Accepted' && (
            <Popup
              content="This application has been accepted"
              trigger={
                <Button color="green" disabled>
                  <Icon name="check" /> Accepted
                </Button>
              }
            />
          )}
          {isCreator && application.status === 'Rejected' && (
            <Popup
              content="This application has been rejected"
              trigger={
                <Button color="red" disabled>
                  <Icon name="times" /> Rejected
                </Button>
              }
            />
          )}
        </div>
      </Card.Content>
    </Card>
  );
};

ApplicationCard.propTypes = {
  application: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    submissionDate: PropTypes.string,
    notes: PropTypes.string,
    actor: PropTypes.shape({
      uuid: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      name: PropTypes.string,
      profilePictureUrl: PropTypes.string
    }),
    samples: PropTypes.arrayOf(
      PropTypes.shape({
        uuid: PropTypes.string.isRequired,
        name: PropTypes.string,
        voiceS3Url: PropTypes.string,
        s3Url: PropTypes.string,
        fileDuration: PropTypes.number
      })
    )
  }).isRequired,
  onViewDetails: PropTypes.func.isRequired,
  onAccept: PropTypes.func,
  onReject: PropTypes.func,
  onMessage: PropTypes.func,
  isCreator: PropTypes.bool,
  viewMode: PropTypes.oneOf(['compact', 'standard', 'detailed']),
  userId: PropTypes.string
};

export default ApplicationCard;
